
import StateBlock from '@/components/ui/StateBlock.vue';
import PanelNoAccess from '@/components/panel/PanelNoAccess.vue';
import { panelRequest, isForbidden, reportPanelError } from '@/components/panel/panelAccess';
import { getConfig, upsertConfig } from "@/services/api/configApi";
import { defineComponent } from 'vue';

interface DeadlineSettings {
  responseHours: number;
  conciliationHours: number;
  juryVotingHours: number;
  jurySelectionHours: number;
  postJuryChoiceHours: number;
  staffVotingHours: number;
  agreementCloseHours: number;
}

interface ScoreSettings {
  reportValidReporter: number;
  reportValidReported: number;
  reportInvalidReporter: number;
  reportInvalidReported: number;
  acceptReported: number;
  juryParticipation: number;
  juryCorrectVote: number;
  juryIncorrectVote: number;
  perSeverityPoint: number;
}

interface JurySettings {
  jurySize: number;
  maxJuryCount: number;
  minVotesRequired: number;
  majorityType: 'simple' | 'super';
  minScore: number;
  minAccountAgeDays: number;
  maxActiveJuryDuties: number;
  excludeStaff: boolean;
  excludeSameFaction: boolean;
  excludeKnownUsers: boolean;
  rotationDays: number;
  honorableMinScore: number;
  honorableMinCorrectVotes: number;
  /** Granted once on Overwatch training completion; also the max for minScore */
  trainingStartScore: number;
}

interface ReductionSettings {
  reductionPercent: number;
  appliesTo: 'severity' | 'quantity' | 'both';
  minSeverityAfterReduction: number;
}

interface GeneralSettings {
  maxPenaltyBlocks: number;
  maxEvidenceItems: number;
  maxJuryPhaseMessages: number;
  allowAnonymousReports: boolean;
  requireEvidence: boolean;
}

interface StaffReviewSettings {
  mode: 'any' | 'auto_assign';
  autoAssignCount: number;
  thresholdPercent: number;
  poolSizeForAny: number;
  thresholdMode: 'majority_of_cast' | 'percent_of_pool';
  minVotes: number;
}

interface AllSettings {
  deadlines: DeadlineSettings;
  scores: ScoreSettings;
  jury: JurySettings;
  staffReview: StaffReviewSettings;
  reduction: ReductionSettings;
  general: GeneralSettings;
}

const defaultSettings: AllSettings = {
  deadlines: {
    responseHours: 48,
    conciliationHours: 72,
    juryVotingHours: 72,
    jurySelectionHours: 24,
    postJuryChoiceHours: 48,
    staffVotingHours: 72,
    agreementCloseHours: 24
  },
  scores: {
    reportValidReporter: 10,
    reportValidReported: -20,
    reportInvalidReporter: -15,
    reportInvalidReported: 5,
    acceptReported: -10,
    juryParticipation: 3,
    juryCorrectVote: 5,
    juryIncorrectVote: -2,
    perSeverityPoint: -2
  },
  jury: {
    jurySize: 5,
    maxJuryCount: 10,
    minVotesRequired: 2,
    majorityType: 'simple',
    minScore: 0,
    minAccountAgeDays: 30,
    maxActiveJuryDuties: 3,
    excludeStaff: true,
    excludeSameFaction: true,
    excludeKnownUsers: true,
    rotationDays: 7,
    honorableMinScore: 50,
    honorableMinCorrectVotes: 10,
    trainingStartScore: 10
  },
  staffReview: {
    mode: 'any',
    autoAssignCount: 5,
    thresholdPercent: 80,
    poolSizeForAny: 10,
    thresholdMode: 'majority_of_cast',
    minVotes: 3
  },
  reduction: {
    reductionPercent: 25,
    appliesTo: 'quantity',
    minSeverityAfterReduction: 1
  },
  general: {
    maxPenaltyBlocks: 5,
    maxEvidenceItems: 10,
    maxJuryPhaseMessages: 5,
    allowAnonymousReports: false,
    requireEvidence: true
  }
};

export default defineComponent({
  name: 'ReportSettingsComponent',
  components: { StateBlock, PanelNoAccess },
  data() {
    return {
      activeTab: 'deadlines',
      initialLoading: true,
      forbidden: false,
      saving: false,
      savingSection: '',
      
      tabs: [
        { id: 'deadlines', label: 'panel.reportSettings.tabs.deadlines', icon: 'fas fa-clock' },
        { id: 'scores', label: 'panel.reportSettings.tabs.scores', icon: 'fas fa-star' },
        { id: 'jury', label: 'panel.reportSettings.tabs.jury', icon: 'fas fa-users' },
        { id: 'staffReview', label: 'panel.reportSettings.tabs.staffReview', icon: 'fas fa-user-shield' },
        { id: 'reduction', label: 'panel.reportSettings.tabs.reduction', icon: 'fas fa-percentage' },
        { id: 'general', label: 'panel.reportSettings.tabs.general', icon: 'fas fa-cog' }
      ],
      
      settings: JSON.parse(JSON.stringify(defaultSettings)) as AllSettings,

      // Overwatch phase timers (REPORT_DEADLINES); STAFF_REVIEW intentionally has none
      overwatchDeadlineFields: [
        { key: 'jurySelectionHours', label: 'report_settings_admin.jury_selection_hours', help: 'report_settings_admin.jury_selection_hours_help', default: 24 },
        { key: 'postJuryChoiceHours', label: 'report_settings_admin.post_jury_choice_hours', help: 'report_settings_admin.post_jury_choice_hours_help', default: 48 },
        { key: 'staffVotingHours', label: 'report_settings_admin.staff_voting_hours', help: 'report_settings_admin.staff_voting_hours_help', default: 72 },
        { key: 'agreementCloseHours', label: 'report_settings_admin.agreement_close_hours', help: 'report_settings_admin.agreement_close_hours_help', default: 24 }
      ],
      
      // Toast
      showToast: false,
      toastTitle: '',
      toastMessage: '',
      toastIcon: 'fas fa-info-circle text-info'
    };
  },
  async mounted() {
    await this.loadAllSettings();
  },
  methods: {
    async loadAllSettings() {
      this.initialLoading = true;
      try {
        // Load each config key
        const configKeys = [
          { key: 'REPORT_DEADLINES', target: 'deadlines' },
          { key: 'USER_SCORE_VALUES', target: 'scores' },
          { key: 'JURY_SETTINGS', target: 'jury' },
          { key: 'STAFF_REVIEW_SETTINGS', target: 'staffReview' },
          { key: 'PENALTY_REDUCTION', target: 'reduction' },
          { key: 'REPORT_SETTINGS', target: 'general' }
        ];
        
        for (const config of configKeys) {
          try {
            const response = await getConfig(config.key, panelRequest) as any;
            if (response?.value) {
              // Merge with defaults to ensure all fields exist
              (this.settings as any)[config.target] = {
                ...JSON.parse(JSON.stringify((defaultSettings as any)[config.target])),
                ...response.value
              };
            }
          } catch (error: any) {
            // Config not found - use defaults
            if (isForbidden(error)) {
              this.forbidden = true;
              break;
            }
            if (error.response?.status !== 404) {
              reportPanelError(`Error loading ${config.key}:`, error);
            }
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        this.showNotification(this.$t('panel.reportSettings.common.error'), this.$t('panel.reportSettings.messages.loadFailed'), 'error');
      } finally {
        this.initialLoading = false;
      }
    },
    
    async saveSettings(section: string) {
      this.saving = true;
      this.savingSection = section;
      
      try {
        let configKey = '';
        let value: any = null;
        let description = '';
        
        switch (section) {
          case 'deadlines':
            configKey = 'REPORT_DEADLINES';
            value = this.settings.deadlines;
            description = 'Deadline durations for report phases';
            break;
          case 'scores':
            configKey = 'USER_SCORE_VALUES';
            value = this.settings.scores;
            description = 'Point values for user score calculations';
            break;
          case 'jury':
            configKey = 'JURY_SETTINGS';
            value = this.settings.jury;
            description = 'Jury system configuration';
            break;
          case 'staffReview':
            configKey = 'STAFF_REVIEW_SETTINGS';
            value = this.settings.staffReview;
            description = 'Staff multi-vote review configuration';
            break;
          case 'reduction':
            configKey = 'PENALTY_REDUCTION';
            value = this.settings.reduction;
            description = 'Penalty reduction for report acceptance';
            break;
          case 'general':
            configKey = 'REPORT_SETTINGS';
            value = this.settings.general;
            description = 'General report system settings';
            break;
        }
        
        if (configKey && value) {
          await upsertConfig({
            key: configKey,
            value: value,
            description: description
          });
          
          this.showNotification(this.$t('panel.reportSettings.common.success'), this.$t('panel.reportSettings.messages.saved', { section: this.getSectionName(section) }), 'success');
        }
      } catch (error: any) {
        const message = error.response?.data?.error || this.$t('panel.reportSettings.messages.saveFailed');
        this.showNotification(this.$t('panel.reportSettings.common.error'), message, 'error');
      } finally {
        this.saving = false;
        this.savingSection = '';
      }
    },
    
    resetDefaults(section: string) {
      switch (section) {
        case 'deadlines':
          this.settings.deadlines = JSON.parse(JSON.stringify(defaultSettings.deadlines));
          break;
        case 'scores':
          this.settings.scores = JSON.parse(JSON.stringify(defaultSettings.scores));
          break;
        case 'jury':
          this.settings.jury = JSON.parse(JSON.stringify(defaultSettings.jury));
          break;
        case 'staffReview':
          this.settings.staffReview = JSON.parse(JSON.stringify(defaultSettings.staffReview));
          break;
        case 'reduction':
          this.settings.reduction = JSON.parse(JSON.stringify(defaultSettings.reduction));
          break;
        case 'general':
          this.settings.general = JSON.parse(JSON.stringify(defaultSettings.general));
          break;
      }
      this.showNotification(this.$t('panel.reportSettings.common.info'), this.$t('panel.reportSettings.messages.reset', { section: this.getSectionName(section) }), 'info');
    },
    
    getSectionName(section: string): string {
      const names: Record<string, string> = {
        deadlines: this.$t('panel.reportSettings.sections.deadlines'),
        scores: this.$t('panel.reportSettings.sections.scores'),
        jury: this.$t('panel.reportSettings.sections.jury'),
        staffReview: this.$t('panel.reportSettings.sections.staffReview'),
        reduction: this.$t('panel.reportSettings.sections.reduction'),
        general: this.$t('panel.reportSettings.sections.general')
      };
      return names[section] || section;
    },
    
    showNotification(title: string, message: string, type: 'success' | 'error' | 'info') {
      this.toastTitle = title;
      this.toastMessage = message;
      switch (type) {
        case 'success':
          this.toastIcon = 'fas fa-check-circle text-success';
          break;
        case 'error':
          this.toastIcon = 'fas fa-times-circle text-danger';
          break;
        default:
          this.toastIcon = 'fas fa-info-circle text-info';
      }
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 4000);
    }
  }
});
